import { ReactNode } from "react";
import { PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";
import { TAX_SYSTEMS_CODES } from "@helpers/taxSystemsCodes";

/** Una de las dos vías para declarar ingresos: el texto de su tarjeta. */
export interface DecisionIngresoVia {
  label: string;
  /** Descripción de la tarjeta; puede llevar `<strong>` como el diseño. */
  sub: ReactNode;
  /** Solo empresas sin `branches`: ruta fija de "Continuar" (navega sin avanzar el status). */
  nextPath?: string;
}

/**
 * Status exclusivos de cada vía. `status_config` es lineal y trae las dos vías; al calcular el
 * siguiente/anterior se brincan los de la vía que no se eligió. Lo que no esté aquí es común a ambas.
 */
export interface DecisionIngresoBranches {
  sat: string[];
  documents: string[];
}

export interface DecisionIngresoConfig {
  subtitle: string;
  /** Conectar con el SAT; siempre lleva la etiqueta "Recomendada". */
  sat: DecisionIngresoVia;
  /** Entregar documentos en lugar de la CIEC. */
  docs: DecisionIngresoVia;
  /**
   * Con `branches`, la decisión avanza el status y el flujo sigue `status_config` (las URLs las da la base).
   * Sin `branches`, "Continuar" va a `nextPath` y "Regresar" a `backPath`, como antes.
   */
  branches?: DecisionIngresoBranches;
  /**
   * Tax system de cada vía (`TAX_SYSTEMS_CODES`), cuando cada una tiene su propio `status_config`. Al continuar se
   * cambia el tax system de la solicitud al de la vía elegida, se recarga el `status_config` y se avanza en él; ya no
   * hace falta `branches`.
   */
  taxSystems?: Record<"sat" | "documents", string>;
  /** Solo empresas sin `branches`: ruta fija de "Regresar". */
  backPath?: string;
}

/**
 * "¿Cómo quieres declarar tus ingresos?" (`DecisionIngreso`). Entre empresas solo cambian los textos de
 * las tarjetas y qué status son de cada vía; la pantalla y su lógica son las mismas. Si una empresa necesita
 * otra estructura o lógica, se crea su pantalla propia, como en `registroDatosPersonalesPorEmpresa`.
 */
const DEFAULT_CONFIG: DecisionIngresoConfig = {
  subtitle:
    "Elige la vía con la que compartirás la información financiera de tu empresa. Puedes cambiar tu selección antes de continuar.",
  backPath: PATHS_TOFU_V2.validacionTelefono.path,
  sat: {
    label: "Conectar con el SAT",
    sub: "Proporciona tu contraseña CIEC y obtenemos tus datos fiscales automáticamente. Es el camino más rápido.",
    nextPath: PATHS_TOFU_V2.consultaCiec.path,
  },
  docs: {
    label: "Entregar documentos",
    sub: "Si prefieres no compartir tu CIEC, sube manualmente los documentos financieros de tu empresa.",
    nextPath: PATHS_TOFU_V2.cargaDocumentos.path,
  },
};

/** Textos del diseño de Mercader (`DecisionProto`): iguales en PFAE y PM. */
const TEXTOS_MERCADER: Pick<DecisionIngresoConfig, "subtitle" | "sat" | "docs"> = {
  subtitle: "Elige la vía con la que compartirás la información financiera del negocio.",
  sat: {
    label: "Con CIEC · conectar con el SAT",
    sub: (
      <>
        Con tu RFC y tu <strong>Contraseña SAT</strong> (antes CIEC) descargamos tu historial fiscal y tu constancia de
        situación fiscal con acceso de <strong>solo lectura</strong>; la contraseña nunca se almacena. Esa misma
        contraseña sirve para autorizar la consulta a Buró de Crédito, sin códigos ni pasos adicionales.
      </>
    ),
  },
  docs: {
    label: "Sin CIEC · cargar documentos",
    sub: "Capturas el domicilio fiscal (sin el SAT no podemos leerlo de tu constancia) y autorizas la consulta a Buró de Crédito con un código de 4 dígitos que te enviamos por SMS. Más adelante, en la etapa de expediente, subirás tus estados de cuenta bancarios, tu constancia de situación fiscal y otros documentos supletorios. El análisis tarda un poco más.",
  },
};

/**
 * Por empresa y, si hace falta, por régimen: `<empresa>_<régimen>` (ej. `mercader_pm`) tiene prioridad sobre
 * `<empresa>` (ver `getDecisionIngresoConfig`).
 */
const DECISION_INGRESO_POR_EMPRESA: Record<string, DecisionIngresoConfig> = {
  // Mercader: cada vía tiene su tax system y su secuencia, iguales hasta la decisión. PFAE: con CIEC la misma
  // contraseña autoriza el buró (de `consulta_ciec` a la sala de espera); sin CIEC captura el domicilio fiscal y
  // autoriza el buró con NIP, y los documentos se piden después, en el expediente.
  mercader: {
    ...TEXTOS_MERCADER,
    taxSystems: { sat: TAX_SYSTEMS_CODES.MERCADER_PFAE, documents: TAX_SYSTEMS_CODES.MERCADER_PFAE_SIN_CIEC },
  },
  // Mercader PM. Las dos vías pasan por el representante legal (datos, domicilio y su buró); con CIEC el buró de la
  // empresa se autoriza en la conexión SAT y sin CIEC con NIP (CDCBE).
  mercader_pm: {
    ...TEXTOS_MERCADER,
    taxSystems: { sat: TAX_SYSTEMS_CODES.MERCADER_PM, documents: TAX_SYSTEMS_CODES.MERCADER_PM_SIN_CIEC },
  },
  // SFI hoy usa la decisión legacy (`tofu/pm/DesicionCiec`), que manda el status a mano. Esto queda listo
  // para cuando pase a v2: mientras su `status_config` no apunte a `/v2`, no se aplica (ver
  // `getIncomeBranchStatusToDiscard`). Pendiente al migrar: en el legacy el texto de documentos cambia
  // según las pestañas del expediente (`getTabsConfig`); aquí va el de documentos SAT.
  sfi: {
    subtitle: "Elige la vía con la que compartirás la información financiera de tu empresa.",
    sat: {
      label: "Conectar con el SAT",
      sub: "Usa tu Contraseña SAT (antes CIEC). Mediante tu RFC más tu clave alfanumérica de 8 caracteres descargamos tu historial fiscal automáticamente con acceso de solo lectura. Tu contraseña nunca se almacena.",
    },
    docs: {
      label: "Cargar documentos",
      sub: "Estados financieros, estados de cuenta y documentos SAT.",
    },
    branches: {
      sat: [STATUS_CATALOG.consulta_ciec.code],
      documents: [STATUS_CATALOG.documentos_sat.code],
    },
  },
};

/**
 * Configuración de la decisión de la empresa (`getConfigName`): primero la de su régimen (`<empresa>_<régimen>`, ej.
 * `mercader_pm`), luego la de la empresa y, si no tiene una propia, la de default.
 */
export const getDecisionIngresoConfig = (configName: string, taxSystemType?: string | null) =>
  (taxSystemType ? DECISION_INGRESO_POR_EMPRESA[`${configName}_${taxSystemType.toLowerCase()}`] : undefined) ??
  DECISION_INGRESO_POR_EMPRESA[configName] ??
  DEFAULT_CONFIG;
